import React, { useState } from "react";
import { SectionLabel } from "./ui";

const ITEMS = [
  { k: "SOC 2 Type II", v: "Annual audit · controls in scope" },
  { k: "ISO 27001", v: "Information security mgmt" },
  { k: "HIPAA aligned", v: "For healthcare deployments" },
  { k: "GDPR · CCPA", v: "Privacy by default" },
  { k: "Private VPC", v: "Bring your own cloud" },
  { k: "Self-host option", v: "Air-gapped enterprise" },
];

export function SecuritySection() {
  return (
    <section id="security" className="relative bg-[#060c22] py-20 border-y border-white/[0.05]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid items-start gap-10 lg:grid-cols-[1fr,2fr]">
          <div>
            <SectionLabel tone="accent">Security &amp; governance</SectionLabel>
            <h2 className="mt-5 text-[28px] leading-[1.1] tracking-tight sm:text-[34px]">
              <span className="text-white">Built for regulated industries</span>{" "}
              <span className="font-display italic grad-text">from day one.</span>
            </h2>
            <p className="mt-4 text-[14.5px] text-white/65 max-w-md">
              Every agent ships with role-based access, immutable audit trails, configurable guardrails and policy attestation. Your data never trains shared models.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.04] md:grid-cols-3">
            {ITEMS.map(i => (
              <div key={i.k} className="bg-[#070d24] p-5">
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-cyan-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round" /><path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <div className="text-[14px] font-semibold text-white">{i.k}</div>
                </div>
                <div className="mt-1 text-[12px] text-white/50">{i.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const FAQ = [
  { q: "How is this different from giving my team ChatGPT or Copilot?", a: "ChatGPT answers; our agents act. They authenticate into your ERP, CRM and downstream systems, execute multi-step workflows against your policies, hand off to humans only when needed, and learn from every outcome. The leverage is operational, not informational." },
  { q: "What does 'self-learning' actually mean — are you fine-tuning a model?", a: "Two things. (1) Agents continuously update a private policy library from successful and failed runs — what worked, what to avoid, what to escalate. (2) For high-volume tasks, we periodically distill those policies into lightweight model adapters. You always own and can inspect both." },
  { q: "Will agents hallucinate or make catastrophic mistakes?", a: "Every agent runs against guardrails you co-design: spend limits, action whitelists, mandatory human approval on certain steps, and full reversibility. Agents launch in shadow mode and only go autonomous on slices we both agree are safe. Every action is logged and explainable." },
  { q: "What if our data lives in legacy systems with no API?", a: "We have 200+ off-the-shelf connectors, including ERPs from the 90s. Where APIs don't exist, agents use vision + RPA fallbacks. We've never failed to integrate." },
  { q: "How fast can we see ROI?", a: "First agent in production within 6 weeks. Most customers see payback inside 5–9 months on Year 1. We track KPIs weekly against a scoreboard signed in Discovery — if we're behind, you don't pay the remaining build fee." },
  { q: "Who owns the agents we build together?", a: "You do. Custom agents, your policies, your data — owned by you, portable on exit. Our IP is the platform that runs them." },
  { q: "Do you replace our people?", a: "We free your people from repetitive work so they can do the work you actually hired them for. The most successful customers redeploy reclaimed hours to revenue-generating roles." },
  { q: "What does 'outcome-linked pricing' look like in practice?", a: "We cap your monthly fee until agreed KPIs hit, then share a small slice of the upside. Available on Scale and Enterprise. Writing it into the contract aligns us with you — if the agent doesn't move the number, we don't get paid extra." },
];

const FAQItem = ({ q, a, open, onClick }) => (
  <div className={`rounded-2xl border ${open ? "border-cyan-300/30 bg-cyan-300/[0.03]" : "border-white/[0.08] bg-white/[0.02]"}`}>
    <button onClick={onClick} className="flex w-full items-start justify-between gap-6 px-6 py-5 text-left">
      <span className={`text-[15.5px] font-medium ${open ? "text-white" : "text-white/85"}`}>{q}</span>
      <span className={`mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border ${open ? "border-cyan-300/40 text-cyan-300 rotate-45" : "border-white/15 text-white/60"} transition-transform`}>
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" strokeLinecap="round" /></svg>
      </span>
    </button>
    {open && <div className="px-6 pb-6 text-[14px] leading-relaxed text-white/65 fadeup">{a}</div>}
  </div>
);

export function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-5xl px-6 lg:px-10">
        <div className="text-center">
          <SectionLabel>Questions buyers actually ask</SectionLabel>
          <h2 className="mt-5 text-[36px] leading-[1.05] tracking-tight sm:text-[42px] lg:text-[48px]">
            <span className="text-white">No hand-waving.</span>{" "}
            <span className="font-display italic grad-text">Direct answers.</span>
          </h2>
        </div>
        <div className="mt-12 space-y-3">
          {FAQ.map((it, i) => (
            <FAQItem key={i} q={it.q} a={it.a} open={open === i} onClick={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
}
